/**
 * Watch List — the people and vehicles this deployment is watching for, and who has been seen.
 *
 * WHY A PAGE, WHEN ALL THREE PIECES ALREADY EXISTED
 * ────────────────────────────────────────────────
 * They existed, but only as things you could do to a record you were already looking at:
 * flagging a person lived in the alert investigation and the register dialogs, the vehicle
 * list opened as a drawer beside a crossing, and the sightings were scattered through the
 * alert history under names like "Blacklisted Person". So "who are we watching for, and has
 * anyone seen them?" — the question the whole feature exists to answer — could only be
 * answered by remembering where each half was kept.
 *
 * WHY ONE PAGE AND NOT TWO
 * ───────────────────────
 * A person and a plate are different records with different lifecycles (see WatchlistPerson),
 * and the temptation is to give each its own page. But an operator does not think "I will go
 * to the vehicle list" — they think "we are watching for this". Splitting by our storage
 * model would publish that model as the interface. Tabs keep one place to go while letting
 * each half keep the editor it already has.
 *
 * WHAT THE LAYOUT IS FOR
 * ─────────────────────
 * ONE selector. The counts an operator arrives for — how many watched, how many seen — ride
 * on the tabs themselves rather than in a row of cards above them: the cards said the same
 * three things the tabs said, switched to the same three places, and highlighted themselves
 * at the same time, so the page had two controls competing to be the one that was selected.
 *
 * WHAT THIS PAGE DOES NOT OWN
 * ──────────────────────────
 * The categories and severities (`watchlistForm`), the person editor (`PersonBlacklistDialog`)
 * and the whole vehicle list (`VehicleBlacklistPanel`, mounted `inline`) are reused verbatim.
 * A second copy of any of them would drift, and the drift would be silent: a category that
 * exists here but not in the register dialog produces alerts nobody can name.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  BellAlertIcon,
  BellSlashIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PlusIcon,
  ShieldExclamationIcon,
  TrashIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { useApi, type WatchlistDetection, type WatchlistPerson } from '../api';
import {
  CLEAR_BUTTON,
  DEFAULT_PAGE_SIZE,
  EmptyRows,
  PRIMARY_BUTTON,
  PageSizePicker,
  RESULTS_CARD,
  ROW,
  ROW_CLICKABLE,
  ResultsFooter,
  SkeletonRows,
  TABLE,
  TD,
  TH,
  THEAD_ROW,
  rowAction,
} from '../components/common/reportSurface';
import { Avatar } from '../components/alerts/Avatar';
import { useAlertImage } from '../components/alerts/useAlertImage';
import RecordModal, { type RecordChrome } from '../components/events/RecordModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import PageTabs from '../components/common/PageTabs';
import Tooltip from '../components/common/Tooltip';
import { PageTitle } from '../components/layout/TitleBar';
import {
  FilterCheck, FilterColumn, FilterSearch, FilterSection, PageWithFilters, useFilterColumn,
} from '../components/layout/FilterColumn';
import VehicleBlacklistPanel from '../components/vehicle/VehicleBlacklistPanel';
import PersonBlacklistDialog, {
  type PersonBlacklistTarget,
} from '../components/people/PersonBlacklistDialog';
import {
  WATCHLIST_PARAMS,
  watchListHref,
  type WatchListTab,
  PERSON_WATCHLIST_CATEGORIES,
  VEHICLE_WATCHLIST_CATEGORIES,
  watchlistCategoryOf,
  watchlistSeverityOf,
} from '../components/common/watchlistForm';

/**
 * Re-exported, not redefined: the type belongs with the URL contract in `watchlistForm`, which
 * is what the dashboard's links are built against. Kept exported from here because callers
 * already import it from the page.
 */
export type { WatchListTab };
/** Re-exported for the same reason as the type — the definition lives with the URL contract. */
export { watchListHref, WATCHLIST_PARAMS };

export interface WatchListPageProps {
  /** False makes the whole page read-only — the lists still show, nothing can be changed. */
  canEdit?: boolean;
  /**
   * Cloud mounts this per node and must not query before one is chosen; blocked renders the
   * "pick a site" state instead of an empty list, which would read as "nobody is watched".
   */
  blocked?: boolean;
  /** Changing it refetches — the selected node, on cloud. */
  nodeKey?: string;
  /** A camera id → its name, for the sightings feed. Cloud and local resolve this differently. */
  cameraName?: (cameraId: string) => string | undefined;
  /** Open a sighting in whatever investigation surface the host app has. */
  onOpenDetection?: (detection: WatchlistDetection) => void;
  /**
   * The sighting's own picture, however this host can reach it — the same pair the alert list
   * takes, and the same hook behind them (`useAlertImage`).
   *
   *   detectionThumbnailFor  a directly-loadable URL. A node's `image_urls` are exactly that.
   *   detectionLoadThumbnail a lazy resolver for a host that cannot load those URLs — the cloud,
   *                          which streams each frame through its tunnel as a blob and only
   *                          when the row is about to be on screen.
   *
   * Omit both and the feed keeps the letter avatar it always had. It is not a lesser page: a
   * sighting whose frame has aged out of retention still happened, and the row still says who,
   * where and when.
   */
  detectionThumbnailFor?: (detection: WatchlistDetection) => string | null;
  detectionLoadThumbnail?: (detection: WatchlistDetection) => Promise<string | null>;
  /**
   * Open a sighting's record OVER the feed rather than navigating to it.
   *
   * The feed is a position an operator works through — a window, a subject filter, a page — and
   * "was that really them?" is asked of one row after another. Navigating away to answer it
   * throws the position away and coming back re-runs the query, so checking the third of twenty
   * sightings costs the other seventeen. With this supplied, a row opens a modal with ← / → over
   * the rows on screen, and `onOpenDetection` becomes its "Full page" button rather than what a
   * click does.
   *
   * A host that supplies neither leaves rows inert; one that supplies only `onOpenDetection`
   * keeps the old navigate-away behaviour, which is what a surface with no modal of its own
   * should do.
   */
  /**
   * Follow this subject on the Track page.
   *
   * Supplied by the host because only it knows its own routes. Omitted, the column is not drawn
   * — a button that goes nowhere is worse than no button.
   */
  onTrackDetection?: (detection: WatchlistDetection) => void;
  detectionDetail?: {
    /** Fetch the typed record behind a sighting. */
    load: (detection: WatchlistDetection) => Promise<any>;
    /**
     * The record's body. No camera names: a host that supplies this already has whatever the
     * body needs — it is the same host that supplies `cameraName` to this page — and handing it
     * a second, emptier copy of its own would be a map that silently resolves nothing. It IS
     * handed the modal's pager / "Full page" / close, which the record draws in its own header.
     */
    render: (event: any, detection: WatchlistDetection, chrome: RecordChrome) => ReactNode;
  };
  className?: string;
}

/** The sightings window. Anything longer belongs in the alert history, which has the filters. */
/**
 * The windows this tab offers, each able to compute its own start.
 *
 * `today` is not a rolling period and cannot be expressed as a number of seconds, which is why
 * `since` is a function rather than `seconds`. It earns its place because it is the window the
 * DASHBOARD counts in: its watch-list cards are "seen today", meaning since local midnight, and
 * the card links straight here. Landing on a rolling 24 hours would show a different number from
 * the one just clicked — a late sighting from yesterday is inside 24h and outside today — and two
 * screens quietly disagreeing about the same question is worse than either being wrong alone.
 *
 * Listed first for the same reason: it is the question an operator arrives with.
 */
const WINDOWS: Array<{ key: string; label: string; since: () => number | undefined }> = [
  {
    key: 'today',
    label: 'Today',
    since: () => {
      const midnight = new Date();
      midnight.setHours(0, 0, 0, 0);
      return Math.floor(midnight.getTime() / 1000);
    },
  },
  { key: '1h', label: 'Last hour', since: () => Math.floor(Date.now() / 1000) - 3600 },
  { key: '24h', label: 'Last 24 hours', since: () => Math.floor(Date.now() / 1000) - 86400 },
  { key: '7d', label: 'Last 7 days', since: () => Math.floor(Date.now() / 1000) - 604800 },
  { key: 'all', label: 'All time', since: () => undefined },
];

/** The Detections subject choices, in the column's order. */
const SUBJECTS: Array<{ key: 'all' | 'person' | 'vehicle'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'person', label: 'People' },
  { key: 'vehicle', label: 'Vehicles' },
];

/** Count, clear and page size above a tab's results — the filters themselves are in the column. */
const TOOLBAR = 'flex flex-wrap items-center justify-end gap-3';

/** The window an unparameterised visit lands on. Unchanged from before the URL contract. */
const DEFAULT_WINDOW = '24h';

/**
 * Parse a parameter against its closed set, falling back to the default.
 *
 * A URL is user input — hand-edited, stale in a bookmark, or truncated by a chat client — so an
 * unrecognised value has to mean "the default", never an empty tab or a filter that matches
 * nothing. Silently showing nothing for `?subject=peple` is the kind of empty result an operator
 * reads as "there were none".
 */
function readTab(raw: string | null): WatchListTab {
  return raw === 'vehicles' || raw === 'detections' || raw === 'people' ? raw : 'people';
}
function readSubject(raw: string | null): 'all' | 'person' | 'vehicle' {
  return raw === 'person' || raw === 'vehicle' ? raw : 'all';
}
function readWindow(raw: string | null): string {
  return WINDOWS.some((w) => w.key === raw) ? (raw as string) : DEFAULT_WINDOW;
}

const relative = (epochSeconds: number): string => {
  const age = Date.now() / 1000 - epochSeconds;
  if (age < 60) return 'just now';
  if (age < 3600) return `${Math.floor(age / 60)} min ago`;
  if (age < 86400) return `${Math.floor(age / 3600)} h ago`;
  return `${Math.floor(age / 86400)} d ago`;
};

const categoriesFor = (kind: 'person' | 'vehicle') =>
  (kind === 'person' ? PERSON_WATCHLIST_CATEGORIES : VEHICLE_WATCHLIST_CATEGORIES);

/** The outlined chip a category draws as — from the shared vocabulary, never a copy. */
function CategoryChip({ kind, value }: { kind: 'person' | 'vehicle'; value: string | null }) {
  const cat = watchlistCategoryOf(categoriesFor(kind), value);
  // A sighting whose list entry has since been deleted has no category, and saying so is more
  // honest than defaulting it to "blacklisted".
  if (!value) {
    return <span className="whitespace-nowrap text-[11px] italic text-muted-foreground">no longer listed</span>;
  }
  return (
    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cat?.chip || 'border border-border text-muted-foreground'}`}>
      {cat?.label || value}
    </span>
  );
}

/** The filled chip a severity draws as. Filled against the category's outline on purpose —
 *  the two sit side by side and answer different questions (see watchlistForm). */
function SeverityChip({ value }: { value: string | null }) {
  const sev = watchlistSeverityOf(value);
  if (!sev) return null;
  return (
    <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${sev.chip}`}>
      {sev.label}
    </span>
  );
}


/**
 * A sighting's picture in the feed, falling back to what the row showed before it had one.
 *
 * WHY A PICTURE AT ALL. The feed answers "has anyone on the list been seen", and the next
 * question is always "is that really them?" — which a name and a camera cannot answer and a face
 * can. A letter avatar beside a matched face is the page holding back the one piece of evidence
 * it has.
 *
 * WHY THE FALLBACK STAYS. Three different situations produce no picture — this host has no
 * resolver, the frame has not arrived yet, and the frame is gone — and all three draw the same
 * avatar. Down a list, a sighting missing its frame for one reason must not look different from
 * one missing it for another, and the operator cannot act on the distinction anyway.
 */
function DetectionThumb({ detection, thumbnailFor, loadThumbnail }: {
  detection: WatchlistDetection;
  thumbnailFor?: (d: WatchlistDetection) => string | null;
  loadThumbnail?: (d: WatchlistDetection) => Promise<string | null>;
}) {
  const { src, markFailed, ref } = useAlertImage(detection, thumbnailFor, loadThumbnail);
  const isPerson = detection.kind === 'person';

  return (
    // The ref rides the BOX, not the image: a lazy resolver has to know the row is coming before
    // there is any <img> to observe.
    <span ref={ref} className="relative flex h-9 w-9 flex-none">
      {src ? (
        <img
          src={src}
          alt=""
          onError={markFailed}
          loading="lazy"
          // object-cover, so a wide frame and a tall face crop both fill the same circle rather
          // than one of them letterboxing into a grey box beside its neighbours.
          className="h-9 w-9 rounded-full object-cover ring-1 ring-border"
        />
      ) : isPerson ? (
        <Avatar name={detection.subject_label || detection.subject_id || '?'} className="h-9 w-9 text-[11px]" />
      ) : (
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
          <TruckIcon className="h-4 w-4 text-muted-foreground" />
        </span>
      )}
    </span>
  );
}

export default function WatchListPage({
  canEdit = true,
  blocked = false,
  nodeKey,
  cameraName,
  onOpenDetection,
  detectionThumbnailFor,
  detectionLoadThumbnail,
  onTrackDetection,
  detectionDetail,
  className = 'space-y-3',
}: WatchListPageProps) {
  const { watchlist, vehicleBlacklist } = useApi();

  /**
   * The tab and the Detections filter live in the URL.
   *
   * Two things need this. The dashboard's watch-list cards link straight to a filtered
   * Detections view — "3 people seen today", clicked, must land on those three and not on the
   * People tab — and a link is the only way one page can say that to another without the two
   * sharing state. And an operator who has narrowed to vehicles seen today can then send that
   * view to a colleague, or reach it again with the back button, which local state cannot do.
   *
   * Read here rather than taken as props because BOTH apps mount this page under react-router
   * and would otherwise each parse the same three parameters, from a contract that lives in this
   * file. `watchListHref` writes them; this reads them; neither can drift from the other.
   */
  const [params, setParams] = useSearchParams();
  const tab = readTab(params.get(WATCHLIST_PARAMS.tab));
  const setTab = useCallback((next: WatchListTab) => {
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      // 'people' is the default, so it is written as the ABSENCE of the parameter. A bare
      // /watch-list and /watch-list?tab=people are then the same URL rather than two.
      if (next === 'people') q.delete(WATCHLIST_PARAMS.tab);
      else q.set(WATCHLIST_PARAMS.tab, next);
      return q;
      // `replace`: switching tabs is moving around inside one page, not navigating between
      // pages, so the back button should leave the watch list rather than walk the tabs.
    }, { replace: true });
  }, [setParams]);

  // ── People ───────────────────────────────────────────────────────────────
  const [people, setPeople] = useState<WatchlistPerson[]>([]);
  const [peopleTotal, setPeopleTotal] = useState(0);
  const [peoplePage, setPeoplePage] = useState(1);
  const [peopleLimit, setPeopleLimit] = useState(DEFAULT_PAGE_SIZE);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [category, setCategory] = useState('');
  const [peopleLoading, setPeopleLoading] = useState(true);
  const [editing, setEditing] = useState<PersonBlacklistTarget | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<WatchlistPerson | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Vehicles (count only — the panel owns the list) ──────────────────────
  const [vehicleCount, setVehicleCount] = useState<number | null>(null);

  // ── Add-from-registry ────────────────────────────────────────────────────
  // Flagging is a property of a REGISTERED person, so adding one is choosing from the
  // registry rather than typing a name — a watchlist entry for a face nobody enrolled could
  // never match anything, and would sit there looking like protection.
  const [picking, setPicking] = useState(false);
  const [registry, setRegistry] = useState<WatchlistPerson[]>([]);
  const [registrySearch, setRegistrySearch] = useState('');
  const [registryLoading, setRegistryLoading] = useState(false);

  // ── Detections ───────────────────────────────────────────────────────────
  const [detections, setDetections] = useState<WatchlistDetection[]>([]);
  const [detectionsTotal, setDetectionsTotal] = useState(0);
  const [detectionsPage, setDetectionsPage] = useState(1);
  const [detectionsLimit, setDetectionsLimit] = useState(DEFAULT_PAGE_SIZE);
  const [detectionsLoading, setDetectionsLoading] = useState(true);
  const subject = readSubject(params.get(WATCHLIST_PARAMS.subject));
  const setSubject = useCallback((next: 'all' | 'person' | 'vehicle') => {
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      if (next === 'all') q.delete(WATCHLIST_PARAMS.subject);
      else q.set(WATCHLIST_PARAMS.subject, next);
      return q;
    }, { replace: true });
  }, [setParams]);

  const [detectionSearch, setDetectionSearch] = useState('');
  const filterCol = useFilterColumn('watch-list');
  /** Which sighting's record is open, as an index into the page on screen. null = closed. */
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  const windowKey = readWindow(params.get(WATCHLIST_PARAMS.window));
  const setWindowKey = useCallback((next: string) => {
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      if (next === DEFAULT_WINDOW) q.delete(WATCHLIST_PARAMS.window);
      else q.set(WATCHLIST_PARAMS.window, next);
      return q;
    }, { replace: true });
  }, [setParams]);
  const [summary, setSummary] = useState<{ person: number; vehicle: number; total: number } | null>(null);

  const since = useMemo(() => {
    const w = WINDOWS.find((x) => x.key === windowKey);
    return w ? w.since() : undefined;
  }, [windowKey]);

  const loadPeople = useCallback(async () => {
    if (blocked) { setPeople([]); setPeopleLoading(false); return; }
    setPeopleLoading(true);
    try {
      const res = await watchlist.people({
        search: peopleSearch || undefined,
        blacklist_category: category || undefined,
        page: peoplePage,
        limit: peopleLimit,
      });
      setPeople(res.data || []);
      setPeopleTotal(res.pagination?.total ?? (res.data || []).length);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the watch list');
      setPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  }, [blocked, category, peopleLimit, peoplePage, peopleSearch, watchlist]);

  const loadVehicleCount = useCallback(async () => {
    if (blocked) { setVehicleCount(null); return; }
    try {
      // One row, for the count the node already computes — the tab's own panel fetches the
      // list, and pulling it twice to put a number on a tab would double the tunnel traffic.
      const res = await vehicleBlacklist.list({ limit: 1 });
      setVehicleCount(res.pagination?.total ?? (res.data || []).length);
    } catch {
      setVehicleCount(null);
    }
  }, [blocked, vehicleBlacklist]);

  const loadDetections = useCallback(async () => {
    if (blocked) { setDetections([]); setDetectionsLoading(false); return; }
    setDetectionsLoading(true);
    try {
      const [res, sum] = await Promise.all([
        watchlist.detections({
          subject: subject === 'all' ? undefined : subject,
          from: since,
          search: detectionSearch || undefined,
          page: detectionsPage,
          limit: detectionsLimit,
        }),
        watchlist.detectionSummary({ from: since }).catch(() => null),
      ]);
      setDetections(res.data || []);
      setDetectionsTotal(res.pagination?.total ?? (res.data || []).length);
      setSummary(sum);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load sightings');
      setDetections([]);
    } finally {
      setDetectionsLoading(false);
    }
  }, [blocked, detectionsLimit, detectionsPage, detectionSearch, since, subject, watchlist]);

  useEffect(() => { void loadPeople(); }, [loadPeople, nodeKey]);
  useEffect(() => { void loadDetections(); }, [loadDetections, nodeKey]);
  useEffect(() => { void loadVehicleCount(); }, [loadVehicleCount, nodeKey]);

  // Narrowing resets to the first page: page 3 of the previous query is a different set of
  // rows, and staying on it shows an empty list for a search that has matches.
  useEffect(() => { setPeoplePage(1); }, [peopleSearch, category, peopleLimit]);
  useEffect(() => { setDetectionsPage(1); }, [subject, windowKey, detectionSearch, detectionsLimit]);

  // An index into a list that has just been replaced points at a different sighting, or at
  // nothing. Closing is the honest response: the reader asked about a row that is no longer the
  // row they were looking at.
  useEffect(() => { setDetailIndex(null); }, [detections]);

  const openRegistry = useCallback(async (search: string) => {
    setRegistryLoading(true);
    try {
      const res = await watchlist.searchRegistry({ search: search || undefined, limit: 25 });
      setRegistry(res.data || []);
    } catch {
      setRegistry([]);
    } finally {
      setRegistryLoading(false);
    }
  }, [watchlist]);

  // The registry lookup is a keystroke away from a query per character, and on cloud it
  // crosses the tunnel. Debounced rather than submit-only: this is the node's own database,
  // so the cost is ours to manage, and picking a name wants to feel like filtering a list.
  useEffect(() => {
    if (!picking) return undefined;
    const t = setTimeout(() => void openRegistry(registrySearch), 250);
    return () => clearTimeout(t);
  }, [openRegistry, picking, registrySearch]);

  const savePerson = async (value: { reason: string | null; category: string; severity: string; alert: boolean }) => {
    if (!editing) return;
    await watchlist.flagPerson(editing.person_id, value);
    setEditing(null);
    await loadPeople();
  };

  const removePerson = async () => {
    if (!editing) return;
    await watchlist.unflagPerson(editing.person_id);
    setEditing(null);
    await loadPeople();
  };

  /**
   * Flagged but SILENT — the person stays on the list and still matches, and the sighting
   * still lands in Detections; it just stops interrupting anybody (migration 054).
   *
   * The row's own act, beside Remove, because it is the vehicle list's "stop watching" in the
   * same slot: the thing you do when somebody should stay on the list but stop shouting.
   * NOT the same act, though — a paused vehicle matches nothing at all — which is why the
   * two wear different icons rather than one icon meaning two things.
   */
  const toggleAlerting = async (p: WatchlistPerson) => {
    try {
      await watchlist.flagPerson(p.person_id, {
        reason: p.blacklist_reason,
        category: p.blacklist_category || undefined,
        severity: p.blacklist_severity || undefined,
        alert: p.blacklist_alert === false,
      });
      await loadPeople();
    } catch (e: any) {
      setError(e?.message || 'Could not change whether this person alerts');
    }
  };

  const doRemove = async () => {
    if (!confirmRemove) return;
    try {
      await watchlist.unflagPerson(confirmRemove.person_id);
      setConfirmRemove(null);
      await loadPeople();
    } catch (e: any) {
      setError(e?.message || 'Could not take this person off the list');
    }
  };

  const filtering = !!peopleSearch || !!category;
  const clearPeopleFilters = () => { setPeopleSearch(''); setCategory(''); };
  const detectionsActive = [subject !== 'all', !!detectionSearch, windowKey !== DEFAULT_WINDOW].filter(Boolean).length;
  const clearDetectionFilters = () => { setSubject('all'); setDetectionSearch(''); setWindowKey(DEFAULT_WINDOW); };

  if (blocked) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card px-4 py-12 text-center">
          <ShieldExclamationIcon className="h-7 w-7 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Select a site to see its watch list.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <PageTitle title="Watch List" subtitle="People and vehicles this site is watching for — and who has been seen.">
        <Tooltip label="Reload both lists and the sightings" side="bottom">
          <button
            type="button"
            data-tour="watchlist-refresh"
            onClick={() => { void loadPeople(); void loadDetections(); void loadVehicleCount(); }}
            aria-label="Refresh"
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </Tooltip>
      </PageTitle>

      {error && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2">
          <p className="text-xs text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => { void loadPeople(); void loadDetections(); }}
            className="flex-none text-xs font-medium text-destructive underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      <PageTabs
        active={tab}
        onChange={(k) => setTab(k as WatchListTab)}
        tabs={[
          { key: 'people', label: 'People', count: peopleLoading ? null : peopleTotal },
          { key: 'vehicles', label: 'Vehicles', count: vehicleCount },
          { key: 'detections', label: 'Detections', count: summary?.total ?? null },
        ]}
      />

      {/* ─── People ─────────────────────────────────────────────────────── */}
      {tab === 'people' && (
        <PageWithFilters
          filters={
            <FilterColumn
              open={filterCol.open}
              onToggle={filterCol.toggle}
              label="Watch list filters"
              activeCount={[!!peopleSearch, !!category].filter(Boolean).length}
              onClearAll={clearPeopleFilters}
            >
              <FilterSection title="Search">
                <FilterSearch value={peopleSearch} onChange={setPeopleSearch} placeholder="Search by name…" />
              </FilterSection>

              {/* Category is the question "what am I looking at" (see watchlistForm). */}
              <FilterSection title="Category">
                <div data-tour="watchlist-category" role="radiogroup" aria-label="Category" className="space-y-0.5">
                  <FilterCheck radio checked={!category} onClick={() => setCategory('')} label="All categories" />
                  {PERSON_WATCHLIST_CATEGORIES.map((c) => (
                    <FilterCheck key={c.value} radio checked={category === c.value} onClick={() => setCategory(c.value)} label={c.label} />
                  ))}
                </div>
              </FilterSection>
            </FilterColumn>
          }
        >
          <div className={TOOLBAR}>
            {filtering && (
              <button type="button" onClick={clearPeopleFilters} className={CLEAR_BUTTON}>
                Clear all
              </button>
            )}

            {canEdit && (
              <button
                type="button"
                data-tour="watchlist-add"
                onClick={() => { setPicking(true); setRegistrySearch(''); void openRegistry(''); }}
                className={PRIMARY_BUTTON}
              >
                <PlusIcon className="h-4 w-4" /> Add person
              </button>
            )}

            <PageSizePicker value={peopleLimit} onChange={setPeopleLimit} />
          </div>

          {/* ── Results ── */}
          <div data-tour="watchlist-results" className={RESULTS_CARD}>
            {peopleLoading ? (
              <SkeletonRows />
            ) : !people.length ? (
              <EmptyRows>
                {/* "Nothing matches your filter" and "nobody is watched for" are different
                    answers, and an operator who cannot tell them apart may believe the site
                    is watching for somebody it is not. */}
                <p>
                  {filtering
                    ? 'No one on the watch list matches the selected filters.'
                    : 'Nobody is on the watch list yet.'}
                </p>
              </EmptyRows>
            ) : (
              <div className="overflow-x-auto">
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th className={TH}>Person</th>
                      <th className={TH}>Category</th>
                      <th className={TH}>Severity</th>
                      <th className={TH}>Alerting</th>
                      <th className={TH}>Reason</th>
                      <th className={TH}>Listed</th>
                      {canEdit && <th className={TH} />}
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((p) => {
                      // A flagged person with no enrolled face can never be matched — the row
                      // says they are watched for and nothing will ever fire. Surfaced here
                      // because it is invisible everywhere else and looks like protection.
                      const unenrolled = p.photo_count === 0;
                      return (
                        <tr
                          key={p.person_id}
                          onClick={canEdit ? () => setEditing(p as PersonBlacklistTarget) : undefined}
                          className={canEdit ? ROW_CLICKABLE : ROW}
                        >
                          <td className={TD}>
                            <span className="flex items-center gap-2.5">
                              <Avatar name={p.name || p.person_id} className="h-8 w-8 flex-none text-[11px]" />
                              <span className="min-w-0">
                                <span className="block truncate font-medium text-foreground">
                                  {p.name || p.person_id}
                                </span>
                                {unenrolled && (
                                  <span
                                    title="No face photo on file — this person cannot be matched, so no sighting will ever fire"
                                    className="mt-0.5 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-medium text-amber-500"
                                  >
                                    <ExclamationTriangleIcon className="h-3 w-3" /> No face enrolled
                                  </span>
                                )}
                              </span>
                            </span>
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            <CategoryChip kind="person" value={p.blacklist_category} />
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            <SeverityChip value={p.blacklist_severity} />
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            {/* Flagged-but-silent is a real configuration (migration 054), so
                                it is shown rather than implied by the category. */}
                            {p.blacklist_alert === false
                              ? <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">Silent</span>
                              : <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-medium text-red-500">Alerts</span>}
                          </td>
                          <td className={`${TD} max-w-[22rem]`}>
                            <span className="block truncate text-muted-foreground" title={p.blacklist_reason || undefined}>
                              {p.blacklist_reason || '—'}
                            </span>
                          </td>
                          <td className={`${TD} whitespace-nowrap text-muted-foreground`}>
                            {p.blacklisted_at
                              ? new Date(p.blacklisted_at).toLocaleDateString()
                              : '—'}
                          </td>
                          {canEdit && (
                            <td className={`${TD} whitespace-nowrap text-right`}>
                              {/* The row opens the editor; these are the two acts the editor
                                  is not — silencing without forgetting, and forgetting.
                                  Stopped from opening it as well, or every click would do
                                  two things. Same slot and same pair as the vehicle rows. */}
                              <span className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => void toggleAlerting(p)}
                                  title={p.blacklist_alert === false
                                    ? 'Alert on a sighting again'
                                    : 'Stay on the list but stop alerting'}
                                  aria-label={p.blacklist_alert === false ? 'Resume alerting' : 'Silence alerts'}
                                  className={`${rowAction()} ${p.blacklist_alert === false ? '' : 'text-red-500'}`}
                                >
                                  {p.blacklist_alert === false
                                    ? <BellSlashIcon className="h-4 w-4" />
                                    : <BellAlertIcon className="h-4 w-4" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmRemove(p)}
                                  title="Take off the watch list"
                                  aria-label="Take off the watch list"
                                  className={rowAction('danger')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!peopleLoading && people.length > 0 && (
              <ResultsFooter
                page={peoplePage}
                totalPages={Math.ceil(peopleTotal / peopleLimit)}
                total={peopleTotal}
                limit={peopleLimit}
                noun="people"
                onPage={setPeoplePage}
              />
            )}
          </div>
        </PageWithFilters>
      )}

      {/* ─── Vehicles — the existing panel, inline ──────────────────────── */}
      {tab === 'vehicles' && (
        <VehicleBlacklistPanel
          layout="inline"
          canEdit={canEdit}
          onChanged={() => { void loadDetections(); void loadVehicleCount(); }}
        />
      )}

      {/* ─── Detections ─────────────────────────────────────────────────── */}
      {tab === 'detections' && (
        <PageWithFilters
          filters={
            <FilterColumn
              open={filterCol.open}
              onToggle={filterCol.toggle}
              label="Sighting filters"
              activeCount={detectionsActive}
              onClearAll={clearDetectionFilters}
            >
              <FilterSection title="Search">
                <FilterSearch value={detectionSearch} onChange={setDetectionSearch} placeholder="Name or plate…" />
              </FilterSection>

              <FilterSection title="Subject">
                <div role="radiogroup" aria-label="Which subjects to show" className="space-y-0.5">
                  {SUBJECTS.map((o) => (
                    <FilterCheck key={o.key} radio checked={subject === o.key} onClick={() => setSubject(o.key)} label={o.label} />
                  ))}
                </div>
              </FilterSection>

              <FilterSection title="Time window">
                <div role="radiogroup" aria-label="Time window" className="space-y-0.5">
                  {WINDOWS.map((w) => (
                    <FilterCheck key={w.key} radio checked={windowKey === w.key} onClick={() => setWindowKey(w.key)} label={w.label} />
                  ))}
                </div>
              </FilterSection>
            </FilterColumn>
          }
        >
          <div className={TOOLBAR}>
            {summary && (
              <span className="mr-auto text-[12px] text-muted-foreground">
                {summary.person} {summary.person === 1 ? 'person' : 'people'} · {summary.vehicle}{' '}
                {summary.vehicle === 1 ? 'vehicle' : 'vehicles'}
              </span>
            )}

            {/* Against DEFAULT_WINDOW, not a repeated '24h': the default is now named once, and
                a link that lands here on `today` must offer to clear back to the same place the
                control's own default is. */}
            {detectionsActive > 0 && (
              <button type="button" onClick={clearDetectionFilters} className={CLEAR_BUTTON}>
                Clear all
              </button>
            )}

            <PageSizePicker value={detectionsLimit} onChange={setDetectionsLimit} />
          </div>

          {/* ── Results ── */}
          <div className={RESULTS_CARD}>
            {detectionsLoading ? (
              <SkeletonRows />
            ) : !detections.length ? (
              <EmptyRows>
                {/* Nothing seen is usually the GOOD answer, so it is said plainly rather than
                    styled as a problem — with the one control that would change it. */}
                <p>No watch-list sightings {detectionSearch ? 'match the selected filters' : 'in this window'}.</p>
                {windowKey !== 'all' && !detectionSearch && (
                  <button
                    type="button"
                    onClick={() => setWindowKey('all')}
                    className="text-[12px] font-medium text-brand underline underline-offset-2"
                  >
                    Look at all time
                  </button>
                )}
              </EmptyRows>
            ) : (
              <div className="overflow-x-auto">
                <table className={TABLE}>
                  <thead>
                    <tr className={THEAD_ROW}>
                      <th className={TH}>Subject</th>
                      <th className={TH}>Category</th>
                      <th className={TH}>Camera</th>
                      <th className={TH}>Direction</th>
                      <th className={TH}>Reason</th>
                      <th className={TH}>Seen</th>
                      {onTrackDetection && <th className={TH} aria-label="Follow" />}
                    </tr>
                  </thead>
                  <tbody>
                    {detections.map((d, i) => {
                      const when = new Date(d.event_timestamp * 1000);
                      return (
                        <tr
                          key={`${d.kind}:${d.event_id}`}
                          /* The modal wins where the host has one — see `detectionDetail`.
                             `onOpenDetection` is not dropped, it becomes the modal's "Full
                             page" button, so the routed record stays reachable. */
                          onClick={detectionDetail
                            ? () => setDetailIndex(i)
                            : onOpenDetection ? () => onOpenDetection(d) : undefined}
                          title={detectionDetail || onOpenDetection
                            ? 'Open the event this sighting came from' : undefined}
                          className={detectionDetail || onOpenDetection ? ROW_CLICKABLE : ROW}
                        >
                          <td className={TD}>
                            <span className="flex items-center gap-2.5">
                              <DetectionThumb
                                detection={d}
                                thumbnailFor={detectionThumbnailFor}
                                loadThumbnail={detectionLoadThumbnail}
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium text-foreground">
                                  {d.subject_label || d.subject_id || 'Unidentified'}
                                </span>
                                <span className="block text-[11px] capitalize text-muted-foreground">
                                  {/* Which reader saw it — an OCR read has no direction, and
                                      that is why its Direction cell is empty. */}
                                  {d.model === 'ocr' ? `${d.kind} · OCR` : d.kind}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td className={`${TD} whitespace-nowrap`}>
                            <CategoryChip kind={d.kind} value={d.category} />
                          </td>
                          <td className={`${TD} max-w-[14rem]`}>
                            <span className="block truncate text-muted-foreground">
                              {cameraName?.(d.camera_id) || d.camera_id}
                            </span>
                          </td>
                          <td className={`${TD} whitespace-nowrap capitalize text-muted-foreground`}>
                            {d.direction || '—'}
                          </td>
                          <td className={`${TD} max-w-[18rem]`}>
                            <span className="block truncate text-muted-foreground" title={d.reason || undefined}>
                              {d.reason || '—'}
                            </span>
                          </td>
                          {/* Relative to read, exact on hover: "3 h ago" is what an operator
                              scans for, and the wall-clock time is what goes in a report. */}
                          <td className={`${TD} whitespace-nowrap text-muted-foreground`} title={when.toLocaleString()}>
                            {relative(d.event_timestamp)}
                          </td>
                          {/*
                            ── FOLLOW THIS ONE ──

                            A sighting answers "was this person here"; the next question is
                            always "where else", and until now that meant retyping the name on
                            another page. The button carries the subject over as a real filter,
                            so the trail that opens is THIS subject's.

                            Only where the subject can be followed across cameras: a name or a
                            plate. An unidentified sighting has no identity to carry — following
                            it would open a page filtered by nothing, which reads as "their whole
                            trail" and is the one wrong answer this button could give.
                          */}
                          {onTrackDetection && (
                            <td className={`${TD} whitespace-nowrap text-right`}>
                              {d.subject_label ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onTrackDetection(d); }}
                                  title={`Follow ${d.subject_label} on the Track page`}
                                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-brand hover:bg-muted"
                                >
                                  <MapPinIcon className="h-3.5 w-3.5" />
                                  Track
                                </button>
                              ) : (
                                <span
                                  className="text-[11px] text-muted-foreground"
                                  title="This sighting has no identity to follow — nothing links it to another camera."
                                >
                                  —
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!detectionsLoading && detections.length > 0 && (
              <ResultsFooter
                page={detectionsPage}
                totalPages={Math.ceil(detectionsTotal / detectionsLimit)}
                total={detectionsTotal}
                limit={detectionsLimit}
                noun="sightings"
                onPage={setDetectionsPage}
              />
            )}
          </div>
        </PageWithFilters>
      )}

      {/* ─── Pick somebody from the registry to flag ────────────────────── */}
      <Modal
        open={picking}
        onClose={() => setPicking(false)}
        title="Add a person to the watch list"
        subtitle="Only registered people can be watched for — a face nobody enrolled can never be matched."
        size="sm"
      >
        <div className="space-y-2">
          <input
            autoFocus
            value={registrySearch}
            onChange={(e) => setRegistrySearch(e.target.value)}
            placeholder="Search registered people"
            aria-label="Search the face registry"
            className="h-9 w-full rounded-lg border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
          {registryLoading ? (
            <div className="flex justify-center py-6"><LoadingSpinner /></div>
          ) : !registry.length ? (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              Nobody matches. A person must be registered before they can be watched for.
            </p>
          ) : (
            <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {registry.map((p) => (
                <li key={p.person_id}>
                  <button
                    type="button"
                    onClick={() => { setPicking(false); setEditing(p as PersonBlacklistTarget); }}
                    className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition-colors hover:bg-muted/40"
                  >
                    <Avatar name={p.name || p.person_id} className="h-7 w-7 flex-none text-[10px]" />
                    <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                      {p.name || p.person_id}
                    </span>
                    {/* Already listed: still selectable, because the gesture then means
                        "change why" — but saying so stops the operator adding a duplicate
                        they will then wonder about. */}
                    {p.is_blacklisted
                      ? <CategoryChip kind="person" value={p.blacklist_category} />
                      : p.photo_count === 0 && (
                        <span
                          title="No face photo on file — flagging them will not match anything until a face is enrolled"
                          className="whitespace-nowrap rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-500"
                        >
                          No face
                        </span>
                      )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmRemove}
        title="Take off the watch list?"
        message={
          <>
            <span className="font-semibold text-foreground">
              {confirmRemove?.name || confirmRemove?.person_id}
            </span>{' '}
            will stop raising alerts. Sightings already recorded keep their record — only the
            listing goes. To stop the alerts and keep the reason, use the bell instead.
          </>
        }
        confirmLabel="Remove"
        variant="danger"
        onConfirm={doRemove}
        onCancel={() => setConfirmRemove(null)}
      />

      <PersonBlacklistDialog
        person={editing}
        onClose={() => setEditing(null)}
        onSubmit={savePerson}
        onRemove={editing?.is_blacklisted ? removePerson : undefined}
      />

      {/* ── The sighting's record, over the feed ──
          Same component the search results use, so a record opened from a watch-list hit and one
          opened from a search read identically — and ← / → step through the sightings on screen
          without losing the window, the subject filter or the page. */}
      {detectionDetail && (
        <RecordModal<WatchlistDetection>
          rows={detections}
          index={detailIndex}
          onIndexChange={setDetailIndex}
          onClose={() => setDetailIndex(null)}
          load={detectionDetail.load}
          /* A sighting IS its event — the two kinds come from different tables, so the kind is
             part of what identifies it. */
          identity={(d) => `${d.kind}:${d.event_id}`}
          fallbackTitle={(d) => (d.kind === 'person' ? 'Person sighting' : 'Vehicle sighting')}
          noun="sightings"
          ariaLabel="Sighting details"
          onOpenFull={onOpenDetection}
          render={detectionDetail.render}
        />
      )}
    </div>
  );
}
